import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../lib/api';
import axios from 'axios';

const AuthContext = createContext(null);

const TOKEN_KEY = 'marketflow_token';
const USER_KEY = 'marketflow_user';
const COMPANY_KEY = 'marketflow_company';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load saved session
  useEffect(() => {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);
    const savedCompany = localStorage.getItem(COMPANY_KEY);

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      if (savedCompany) {
        setCompany(JSON.parse(savedCompany));
      }
      
      // Verify token is still valid
      verifyToken(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await axios.get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${tokenToVerify}` }
      });
      setUser(response.data.user);
      setCompany(response.data.company);
      localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
      if (response.data.company) {
        localStorage.setItem(COMPANY_KEY, JSON.stringify(response.data.company));
      }
    } catch (error) {
      // Token invalid, clear session
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_BASE}/auth/login`, { email, password });
    const { token: newToken, user: newUser, company: newCompany } = response.data;
    
    setToken(newToken);
    setUser(newUser);
    setCompany(newCompany);
    
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    if (newCompany) {
      localStorage.setItem(COMPANY_KEY, JSON.stringify(newCompany));
    }
    
    return response.data;
  }, []);

  const register = useCallback(async (data) => {
    const response = await axios.post(`${API_BASE}/auth/register`, data);
    const { token: newToken, user: newUser, company: newCompany } = response.data;
    
    setToken(newToken);
    setUser(newUser);
    setCompany(newCompany);
    
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    if (newCompany) {
      localStorage.setItem(COMPANY_KEY, JSON.stringify(newCompany));
    }
    
    return response.data;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setCompany(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(COMPANY_KEY);
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
  }, []);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager' || isAdmin;
  const isAuthenticated = !!token && !!user;

  return (
    <AuthContext.Provider value={{
      user,
      company,
      token,
      loading,
      isAuthenticated,
      isAdmin,
      isManager,
      login,
      register,
      logout,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
